import { format } from 'date-fns';
import { CalendarIcon } from 'lucide-react';
import { useState } from 'react';

import { Calendar } from '~shared/shadcn/calendar';
import { cn } from '~shared/shadcn/lib/utils';
import { Button } from '~shared/shadcn/ui/button';
import { Checkbox } from '~shared/shadcn/ui/checkbox';
import { Label } from '~shared/shadcn/ui/label';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '~shared/shadcn/ui/popover';

import { type InstructorOption } from '../lib/types';
import { RoomOverviewInstructorFilter } from './room-overview-instructor-filter';

interface RoomOverviewFiltersProps {
  selectedDate: Date;
  onDateChange: (date: Date) => void;
  instructors: Array<InstructorOption>;
  onInstructorsChange: (instructors: Array<InstructorOption>) => void;
  showEmptyRooms: boolean;
  onShowEmptyRoomsChange: (show: boolean) => void;
}

export function RoomOverviewFiltersBar({
  selectedDate,
  onDateChange,
  instructors,
  onInstructorsChange,
  showEmptyRooms,
  onShowEmptyRoomsChange,
}: RoomOverviewFiltersProps) {
  const [datePickerOpen, setDatePickerOpen] = useState(false);

  return (
    <div className='flex w-full flex-col gap-4 sm:flex-row sm:flex-wrap sm:items-end'>
      <div className='flex w-full flex-col gap-1.5 sm:w-auto'>
        <Label className='w-fit text-xs font-medium' htmlFor='date'>
          Date
        </Label>
        <Popover open={datePickerOpen} onOpenChange={setDatePickerOpen}>
          <PopoverTrigger asChild>
            <Button
              id='date'
              variant='outline'
              className={cn(
                'h-10 w-full justify-start text-left font-normal sm:w-48',
              )}
            >
              <CalendarIcon className='mr-2 size-4' />
              {format(selectedDate, 'PPP')}
            </Button>
          </PopoverTrigger>
          <PopoverContent className='z-[100] w-auto p-0' align='start'>
            <Calendar
              mode='single'
              selected={selectedDate}
              onSelect={(date) => {
                onDateChange(date);
                setDatePickerOpen(false);
              }}
              required
            />
          </PopoverContent>
        </Popover>
      </div>

      <div className='flex w-full min-w-0 flex-col gap-1.5 sm:min-w-[220px] sm:w-auto'>
        <Label className='w-fit text-xs font-medium' htmlFor='instructors'>
          Instructors
        </Label>
        <div id='instructors' className='w-full'>
          <RoomOverviewInstructorFilter
            value={instructors}
            onChange={onInstructorsChange}
          />
        </div>
      </div>

      <div className='flex h-10 items-end gap-2'>
        <Checkbox
          id='show-empty-rooms'
          checked={showEmptyRooms}
          onCheckedChange={(checked) => {
            onShowEmptyRoomsChange(checked === true);
          }}
        />
        <Label
          htmlFor='show-empty-rooms'
          className='cursor-pointer text-sm font-normal'
        >
          Show empty rooms
        </Label>
      </div>
    </div>
  );
}
